// src/scenes/sitemanager/DashboardFinance.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    Card,
    CardContent,
    Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import StatBox from "../../component-files/StatBox";
import Header from "../../component-files/Header";
import { tokens } from "../../theme";

const pad2 = (n) => String(n).padStart(2, "0");
const today = new Date();
const todayYM = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}`;
const ALL = "__ALL__";

export default function DashboardFinance() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // filters
    const [siteFilter, setSiteFilter] = useState(ALL);
    const [yearFilter, setYearFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");

    // raw data
    const [incData, setIncData] = useState([]);
    const [expData, setExpData] = useState([]);

    // dropdown options
    const [sites, setSites] = useState([]);
    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);

    useEffect(() => {
        (async () => {
            const [incRes, expRes] = await Promise.all([
                axios.get("https://figazuresql.swancreate.com/api/swa/vw_KPI_IncomePerMEQBySiteMonth"),
                axios.get("https://figazuresql.swancreate.com/api/swa/vw_KPI_ExpensePerMEQBySiteMonth"),
            ]);

            // normalize & cut off future months
            const inc = (incRes.data || [])
                .map((r) => ({
                    SchoolID: r.SchoolID,
                    SchoolName: r.SchoolName,
                    YearMonth: String(r.YearMonth).replace(/\s+/g, ""),
                    TotalIncomes: Number(r.TotalIncomes ?? 0),
                    TotalMEQ: Number(r.TotalMEQ ?? 0),
                    IncomePerMEQ: Number(r.IncomePerMEQ ?? 0),
                }))
                .filter((r) => r.YearMonth <= todayYM);

            const exp = (expRes.data || [])
                .map((r) => ({
                    SchoolID: r.SchoolID,
                    SchoolName: r.SchoolName,
                    YearMonth: String(r.YearMonth).replace(/\s+/g, ""),
                    TotalExpenses: Number(r.TotalExpenses ?? 0),
                    TotalMEQ: Number(r.TotalMEQ ?? 0),
                    ExpensePerMEQ: Number(r.ExpensePerMEQ ?? 0),
                }))
                .filter((r) => r.YearMonth <= todayYM);

            // build ALL sites (use SchoolID, not SchoolId), alpha sort + "All Sites"
            const siteMap = new Map();
            [...inc, ...exp].forEach((r) => {
                if (r.SchoolID) siteMap.set(r.SchoolID, r.SchoolName);
            });
            const sortedSites = Array.from(siteMap, ([id, name]) => ({ id, name })).sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            const siteOpts = [{ id: ALL, name: "All Sites" }, ...sortedSites];
            setSites(siteOpts);
            if (!siteOpts.find((s) => s.id === siteFilter)) setSiteFilter(ALL);

            // build years & months from income view
            const ySet = new Set();
            const mSet = new Set();
            inc.forEach((r) => {
                const [y] = r.YearMonth.split("-");
                ySet.add(y);
                mSet.add(r.YearMonth);
            });
            const yearsArr = Array.from(ySet).sort();
            const monthsArr = Array.from(mSet).sort();
            setYears(yearsArr);
            setMonths(monthsArr);
            if (!yearFilter || !yearsArr.includes(yearFilter)) {
                setYearFilter(yearsArr[yearsArr.length - 1] || "");
            }
            if (monthFilter && !monthsArr.includes(monthFilter)) setMonthFilter("");

            setIncData(inc);
            setExpData(exp);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // filtered slices (use SchoolID everywhere)
    const filteredInc = useMemo(() => {
        return incData.filter((r) => {
            const siteOK = siteFilter === ALL ? true : r.SchoolID === siteFilter;
            const yearOK = yearFilter ? r.YearMonth.startsWith(yearFilter) : true;
            const monthOK = monthFilter ? r.YearMonth === monthFilter : true;
            return siteOK && yearOK && monthOK;
        });
    }, [incData, siteFilter, yearFilter, monthFilter]);

    const filteredExp = useMemo(() => {
        return expData.filter((r) => {
            const siteOK = siteFilter === ALL ? true : r.SchoolID === siteFilter;
            const yearOK = yearFilter ? r.YearMonth.startsWith(yearFilter) : true;
            const monthOK = monthFilter ? r.YearMonth === monthFilter : true;
            return siteOK && yearOK && monthOK;
        });
    }, [expData, siteFilter, yearFilter, monthFilter]);

    // totals
    const totalIncome = filteredInc.reduce((s, r) => s + r.TotalIncomes, 0);
    const totalMEQ = filteredInc.reduce((s, r) => s + r.TotalMEQ, 0);
    const totalExpense = filteredExp.reduce((s, r) => s + r.TotalExpenses, 0);

    // leaderboards (YTD by site for selected year)
    const incLeaderboard = useMemo(() => {
        const map = new Map(); // id -> {site, inc, meq}
        incData
            .filter((r) => (yearFilter ? r.YearMonth.startsWith(yearFilter) : true))
            .forEach((r) => {
                const g = map.get(r.SchoolID) || { site: r.SchoolName, inc: 0, meq: 0 };
                g.inc += r.TotalIncomes;
                g.meq += r.TotalMEQ;
                map.set(r.SchoolID, g);
            });
        return Array.from(map.values())
            .map((g) => ({ site: g.site, perMEQ: g.meq > 0 ? g.inc / g.meq : 0 }))
            .sort((a, b) => b.perMEQ - a.perMEQ);
    }, [incData, yearFilter]);

    const expLeaderboard = useMemo(() => {
        const map = new Map(); // id -> {site, exp, meq}
        expData
            .filter((r) => (yearFilter ? r.YearMonth.startsWith(yearFilter) : true))
            .forEach((r) => {
                const g = map.get(r.SchoolID) || { site: r.SchoolName, exp: 0, meq: 0 };
                g.exp += r.TotalExpenses;
                g.meq += r.TotalMEQ;
                map.set(r.SchoolID, g);
            });
        return Array.from(map.values())
            .map((g) => ({ site: g.site, perMEQ: g.meq > 0 ? g.exp / g.meq : 0 }))
            .sort((a, b) => b.perMEQ - a.perMEQ);
    }, [expData, yearFilter]);

    const totalIncSites = incLeaderboard.length;
    const totalExpSites = expLeaderboard.length;

    // rank (only when a single site is selected)
    const incRank = useMemo(() => {
        if (siteFilter === ALL) return null;
        const name = sites.find((s) => s.id === siteFilter)?.name;
        const idx = incLeaderboard.findIndex((d) => d.site === name);
        return idx >= 0 ? idx + 1 : null;
    }, [incLeaderboard, siteFilter, sites]);

    const expRank = useMemo(() => {
        if (siteFilter === ALL) return null;
        const name = sites.find((s) => s.id === siteFilter)?.name;
        const idx = expLeaderboard.findIndex((d) => d.site === name);
        return idx >= 0 ? idx + 1 : null;
    }, [expLeaderboard, siteFilter, sites]);

    // monthly grid (aggregate across sites when "All Sites")
    const monthlyRows = useMemo(() => {
        const byMonth = new Map(); // ym -> {inc, exp, meqInc, meqExp}
        filteredInc.forEach((r) => {
            const g = byMonth.get(r.YearMonth) || { inc: 0, exp: 0, meqI: 0, meqE: 0 };
            g.inc += r.TotalIncomes;
            g.meqI += r.TotalMEQ;
            byMonth.set(r.YearMonth, g);
        });
        filteredExp.forEach((r) => {
            const g = byMonth.get(r.YearMonth) || { inc: 0, exp: 0, meqI: 0, meqE: 0 };
            g.exp += r.TotalExpenses;
            g.meqE += r.TotalMEQ;
            byMonth.set(r.YearMonth, g);
        });

        return Array.from(byMonth.entries())
            .sort(([a], [b]) => (a < b ? -1 : 1))
            .map(([m, g]) => {
                const incPM = g.meqI > 0 ? g.inc / g.meqI : 0;
                const expPM = g.meqE > 0 ? g.exp / g.meqE : 0;
                return {
                    id: m,
                    month: m,
                    income: g.inc,
                    incPerMEQ: incPM,
                    expense: g.exp,
                    expPerMEQ: expPM,
                };
            });
    }, [filteredInc, filteredExp]);

    const detailCols = [
        { field: "month", headerName: "Month", width: 120 },
        {
            field: "income",
            headerName: "Income ($)",
            width: 140,
            type: "number",
            valueFormatter: ({ value }) => `$${(value ?? 0).toLocaleString()}`,
        },
        {
            field: "incPerMEQ",
            headerName: "Income/MEQ",
            width: 130,
            type: "number",
            valueFormatter: ({ value }) => (value ?? 0).toFixed(2),
        },
        {
            field: "expense",
            headerName: "Expense ($)",
            width: 140,
            type: "number",
            valueFormatter: ({ value }) => `$${(value ?? 0).toLocaleString()}`,
        },
        {
            field: "expPerMEQ",
            headerName: "Expense/MEQ",
            width: 130,
            type: "number",
            valueFormatter: ({ value }) => (value ?? 0).toFixed(2),
        },
    ];

    const downloadMonthlyCSV = () => {
        const siteName = siteFilter === ALL ? "All Sites" : (sites.find((s) => s.id === siteFilter)?.name || "");
        const header = ["Site", "Month", "Income", "Income/MEQ", "Expense", "Expense/MEQ"];
        const csv = [
            header.join(","),
            ...monthlyRows.map((r) =>
                [
                    `"${siteName}"`,
                    r.month,
                    r.income.toFixed(2),
                    r.incPerMEQ.toFixed(2),
                    r.expense.toFixed(2),
                    r.expPerMEQ.toFixed(2),
                ].join(",")
            ),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "income_expense_per_meq.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box p={3}>
            <Header title="Finance Dashboard" subtitle="Income vs Expense per MEQ" />

            {/* Filters */}
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Site</InputLabel>
                    <Select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
                        {sites.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Year</InputLabel>
                    <Select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                        {years.map((y) => (
                            <MenuItem key={y} value={y}>
                                {y}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Month</InputLabel>
                    <Select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        {months
                            .filter((m) => (yearFilter ? m.startsWith(yearFilter) : true))
                            .map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Totals */}
            <Box display="grid" gridTemplateColumns="repeat(3,1fr)" gap={2} mb={4}>
                <StatBox
                    title={`$${totalIncome.toLocaleString()}`}
                    subtitle={siteFilter === ALL ? "Total Income (All Sites)" : "Total Income"}
                    borderColor={colors.greenAccent[500]}
                />
                <StatBox
                    title={`${totalMEQ.toFixed(2)}`}
                    subtitle={siteFilter === ALL ? "Total MEQs (All Sites)" : "Total MEQs"}
                    borderColor={colors.blueAccent[500]}
                />
                <StatBox
                    title={`$${totalExpense.toLocaleString()}`}
                    subtitle={siteFilter === ALL ? "Total Expense (All Sites)" : "Total Expense"}
                    borderColor={colors.redAccent[500]}
                />
            </Box>

            {/* Leaderboards + Rank Boxes */}
            <Box display="flex" gap={2} mb={4} flexWrap="wrap">
                {/* Income/MEQ */}
                <Box flex={1} minWidth={320}>
                    {incRank && (
                        <Box
                            sx={{
                                p: 4,
                                mb: 2,
                                borderRadius: 3,
                                textAlign: "center",
                                boxShadow: `0 0 20px ${colors.greenAccent[500]}`,
                            }}
                        >
                            <Typography variant="h6">
                                {sites.find((s) => s.id === siteFilter)?.name}
                            </Typography>
                            <Typography variant="h1" fontWeight="bold" mb={1}>
                                {incRank}
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom>
                                rank out of {totalIncSites}
                            </Typography>
                            <Typography variant="subtitle2" color={colors.greenAccent[700]}>
                                Income/MEQ
                            </Typography>
                        </Box>
                    )}

                    <Typography variant="h6" gutterBottom>
                        🏆 Top Income/MEQ (All Sites)
                    </Typography>
                    <Box
                        component="ol"
                        sx={{
                            pl: 2,
                            m: 0,
                            maxHeight: 300,
                            overflowY: "auto",
                            "& li": {
                                display: "flex",
                                justifyContent: "space-between",
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                            },
                            "& li:nth-of-type(odd)": {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        {incLeaderboard.map((d, i) => (
                            <Typography
                                key={`inc-${d.site}-${i}`}
                                component="li"
                                sx={{
                                    fontWeight:
                                        siteFilter !== ALL &&
                                            d.site === sites.find((s) => s.id === siteFilter)?.name
                                            ? "bold"
                                            : "inherit",
                                }}
                            >
                                <span>{`${i + 1}. ${d.site}`}</span>
                                <span>{d.perMEQ.toFixed(2)}</span>
                            </Typography>
                        ))}
                    </Box>
                </Box>

                {/* Expense/MEQ */}
                <Box flex={1} minWidth={320}>
                    {expRank && (
                        <Box
                            sx={{
                                p: 4,
                                mb: 2,
                                borderRadius: 3,
                                textAlign: "center",
                                boxShadow: `0 0 20px ${colors.redAccent[500]}`,
                            }}
                        >
                            <Typography variant="h6">
                                {sites.find((s) => s.id === siteFilter)?.name}
                            </Typography>
                            <Typography variant="h1" fontWeight="bold" mb={1}>
                                {expRank}
                            </Typography>
                            <Typography variant="subtitle1" gutterBottom>
                                rank out of {totalExpSites}
                            </Typography>
                            <Typography variant="subtitle2" color={colors.redAccent[700]}>
                                Expense/MEQ
                            </Typography>
                        </Box>
                    )}

                    <Typography variant="h6" gutterBottom>
                        🏆 Top Expense/MEQ (All Sites)
                    </Typography>
                    <Box
                        component="ol"
                        sx={{
                            pl: 2,
                            m: 0,
                            maxHeight: 300,
                            overflowY: "auto",
                            "& li": {
                                display: "flex",
                                justifyContent: "space-between",
                                py: 0.5,
                                px: 1,
                                borderRadius: 1,
                            },
                            "& li:nth-of-type(odd)": {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        {expLeaderboard.map((d, i) => (
                            <Typography
                                key={`exp-${d.site}-${i}`}
                                component="li"
                                sx={{
                                    fontWeight:
                                        siteFilter !== ALL &&
                                            d.site === sites.find((s) => s.id === siteFilter)?.name
                                            ? "bold"
                                            : "inherit",
                                }}
                            >
                                <span>{`${i + 1}. ${d.site}`}</span>
                                <span>{d.perMEQ.toFixed(2)}</span>
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Monthly Details */}
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">Monthly Details</Typography>
                        <Button
                            variant="contained"
                            onClick={downloadMonthlyCSV}
                            sx={{
                                backgroundColor: theme.palette.success.main,
                                color: theme.palette.getContrastText(theme.palette.success.main),
                                "&:hover": { backgroundColor: theme.palette.success.dark },
                            }}
                        >
                            Download CSV
                        </Button>
                    </Box>
                    <Box sx={{ height: 500, width: "100%" }}>
                        <DataGrid
                            rows={monthlyRows}
                            columns={detailCols}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            pagination
                            disableSelectionOnClick
                            sx={{
                                "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
